// Fremd-App im Vordergrund erkennen und melden (nur Samsung Tizen).
//
// Anlass: Am HU800F lag kurz nach dem ersten Start - Registrierung durch,
// Hauptmenue sichtbar - ploetzlich Samsung TV Plus mit einem Sender ueber der
// App. Gestartet hatte ihn nicht die App, sondern die Firmware (vermutlich die
// Autostart-Einstellungen aus dem Consumer-Modus nach TEP aus/an). Erst HOME
// holte ueber die Start-App die tv-app zurueck. Im Feld sieht das niemand -
// ausser dem Gast.
//
// Dieser Helfer BEOBACHTET nur: er schaltet nichts, startet nichts und holt
// die App nicht nach vorn. Er merkt sich, wann die App in den Hintergrund
// ging, und entscheidet bei ihrer Rueckkehr, ob das erklaerbar war:
//
//   - Standby          -> prepareCastStandby hat noteStandby gerufen
//                         (remotepower / b2bpower / isRealStandby).
//   - eigener Start    -> cast/launchApp, launchCastEntry oder noteAppHandoff
//                         (Browser) haben kurz vorher noteOwnLaunch gerufen.
//
// Bleibt nichts davon, ist es eine Fremd-App: eine Logzeile und ein Eintrag
// `foreignApp` in der state-Meldung an den Socket (siehe socket/reportState).
//
// Die Rueckkehr kommt auf zwei Wegen: als visibilitychange, oder - wenn HOME
// ueber die Start-App die tv-app neu laedt - als frischer App-Start. Deshalb
// liegt der offene Vermerk im localStorage; bei einem Neustart wird er
// ausgewertet. localStorage kann fehlen oder werfen - dann faellt nur der
// Neustart-Weg aus, sonst nichts.

const PENDING_KEY = 'foreignApp.pending';
const LAST_KEY = 'foreignApp.last';

// Ein eigener App-Start gilt so lange als Erklaerung fuer den naechsten
// Wechsel in den Hintergrund. Der Wechsel folgt dem Start in Sekunden; eine
// Minute laesst langsamen Firmwares Luft.
export const OWN_LAUNCH_WINDOW_MS = 60 * 1000;
// Laenger weg als das, ohne Standby-Meldung: nicht mehr sicher einzuordnen
// (z.B. Stromausfall, Standby ohne Listener). Lieber nichts melden als falsch.
export const MAX_AWAY_MS = 30 * 60 * 1000;
// Der Power-Listener kann auch VOR visibilitychange feuern. Ein Standby so
// kurz vor dem Wechsel in den Hintergrund erklaert ihn ebenfalls.
export const STANDBY_WINDOW_MS = 15 * 1000;

let ownLaunchAt = 0;
let standbyAt = 0;
let pending = null;
let last = null;
let started = false;
let appStartAt = Date.now();
let hooks = { route: () => null, report: () => {} };

function readJson(key) {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}
function writeJson(key, value) {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    // ignorieren - siehe oben
  }
}

function isTizen() {
  return typeof window !== 'undefined' && !!window.tizen;
}

function ownAppId() {
  try {
    return window.tizen.application.getCurrentApplication().appInfo.id;
  } catch (e) {
    return null;
  }
}

// Welche Apps laufen gerade? Die Firmware sagt nicht, welche vorne ist - aber
// eine neu auftauchende App (z.B. TV Plus) ist genau die gesuchte Spur.
// Rein lesend; bleibt der Callback aus, fehlt nur das Feld.
function collectRunningApps() {
  const target = pending;
  try {
    const app = window.tizen && window.tizen.application;
    if (!app || typeof app.getAppsContext !== 'function') return;
    const self = ownAppId();
    app.getAppsContext(
      (contexts) => {
        const apps = (contexts || [])
          .map((c) => c && c.appId)
          .filter((id) => id && id !== self);
        if (pending === target) {
          pending.apps = apps;
          writeJson(PENDING_KEY, pending);
          return;
        }
        // Die App war im Hintergrund pausiert, der Callback kommt erst nach
        // der Rueckkehr: dann gehoert die Liste zum schon erzeugten Eintrag.
        // Ein spaeter Callback darf keinen neueren Fall ueberschreiben.
        if (last && target && last.at === target.hiddenAt && !last.apps) {
          last.apps = apps;
          writeJson(LAST_KEY, last);
          reportNow();
        }
      },
      () => {}
    );
  } catch (e) {
    // ignorieren
  }
}

// Reine Entscheidung, ohne Seiteneffekte (scripts/check-foreign-app.js).
//
// Rueckgabe: null, wenn der Wechsel erklaert ist, sonst der Meldungs-Eintrag.
export function classify(p, now, back) {
  if (!p || !p.hiddenAt) return null;
  if (p.standby) return null;
  if (p.ownLaunchAt && p.hiddenAt - p.ownLaunchAt <= OWN_LAUNCH_WINDOW_MS) return null;
  const awayMs = now - p.hiddenAt;
  if (awayMs < 0 || awayMs > MAX_AWAY_MS) return null;
  const entry = {
    at: p.hiddenAt,
    awayMs: awayMs,
    back: back,
    route: p.route || null,
    sinceStartMs: p.sinceStartMs,
  };
  if (p.apps && p.apps.length) entry.apps = p.apps;
  return entry;
}

function reportNow() {
  try {
    hooks.report();
  } catch (e) {
    // ignorieren
  }
}

function finish(back) {
  const p = pending;
  pending = null;
  writeJson(PENDING_KEY, null);
  const entry = classify(p, Date.now(), back);
  if (!entry) return;
  last = entry;
  writeJson(LAST_KEY, entry);
  console.log(
    '[ForeignApp] App war ' + Math.round(entry.awayMs / 1000) + ' s verdeckt, ' +
      'weder Standby noch eigener App-Start (Seite: ' + (entry.route || '?') +
      ', ' + Math.round(entry.sinceStartMs / 1000) + ' s nach App-Start, zurueck per ' +
      back + ', laufende Apps: ' + (entry.apps ? entry.apps.join(', ') : '?') + ')'
  );
  reportNow();
}

function onVisibility() {
  if (document.hidden) {
    let route = null;
    try {
      route = hooks.route();
    } catch (e) {
      // ignorieren
    }
    const now = Date.now();
    pending = {
      hiddenAt: now,
      sinceStartMs: now - appStartAt,
      route: route,
      ownLaunchAt: ownLaunchAt || 0,
      standby: !!standbyAt && now - standbyAt <= STANDBY_WINDOW_MS,
    };
    writeJson(PENDING_KEY, pending);
    collectRunningApps();
  } else if (pending) {
    finish('visibilitychange');
  }
}

const ForeignApp = {
  // Einmal beim App-Start. route() liefert den Namen der aktuellen Seite,
  // report() stoesst die state-Meldung an.
  start(opts) {
    if (started || !isTizen()) return;
    started = true;
    appStartAt = Date.now();
    hooks = Object.assign(hooks, opts || {});
    last = readJson(LAST_KEY);
    // Offener Vermerk vom letzten Lauf: die App wurde verdeckt und kam per
    // Neustart zurueck (HOME -> Start-App).
    pending = readJson(PENDING_KEY);
    if (pending) finish('restart');
    document.addEventListener('visibilitychange', onVisibility);
  },
  // Die App gibt den Vordergrund selbst ab (Cast, Browser).
  noteOwnLaunch() {
    ownLaunchAt = Date.now();
    if (pending) {
      pending.ownLaunchAt = ownLaunchAt;
      writeJson(PENDING_KEY, pending);
    }
  },
  // Ein bestaetigter Standby erklaert den laufenden Wechsel.
  noteStandby() {
    standbyAt = Date.now();
    if (!pending) return;
    pending.standby = true;
    writeJson(PENDING_KEY, pending);
  },
  // Letzter Fremd-App-Fall fuer die state-Meldung, oder null.
  last() {
    return last;
  },
};

export default ForeignApp;



// WEBPACK FOOTER //
// ./src/helpers/ForeignApp.js